/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Primary navigation rail — customisable, persisted sidebar. ONE shared
// implementation for both web (react-native-web) and desktop
// (react-native-macos); they behave identically. Pixel-matched to Figma
// "Nav bar/desktop" (4582:83174) and the "Tools menu" popover
// (3393:217233 / 4582:82601).
//
// The tools catalog is the exact Figma set:
//   Fixed (always pinned, top of rail): Chat / History / Contacts
//   Pinnable tools:  Directory / Job Board / Features / Vault / Events / Calendar
//
// Layout (rail 64px; items 48×48 with a 10px label):
//   Fixed pinned → user-pinned tools → divider → active unpinned tool
//   ⸺ bottom ⸺ All tools button → account avatar
//
// Interactions (same on every platform):
//   • Click "All tools" → AnchoredPopover grid of every tool, opening to
//     the RIGHT. Click a tool → navigate. Right-click (web) / long-press →
//     a small Pin / Unpin AnchoredPopover (Figma options-menu 4582:82734).
//   • Press-and-hold a customisable rail item → edit mode; on web drag to
//     reorder (real DOM pointer events + elementFromPoint). "Done" exits.
//
// Navigation for each tool is supplied by the host via `onNavigate`
// (canonical destinations) and `extras` (host routes, keyed by tool key).
// Tools without a wired route render exactly but no-op until one exists.

import { X } from "lucide-react-native";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Image, Platform, Pressable, Text, View } from "react-native";
import { AnchoredPopover } from "../components/AnchoredPopover";
import { LinearGradient } from "../components/LinearGradient";
import { tokens } from "../design-system/tokens";
import { useColorScheme } from "../foundations/colorScheme";
import { NavIcon, type NavIconName } from "../icons";
import { type Destination, type SidebarProps } from "./Sidebar.types";

const IS_WEB = Platform.OS === "web";
const LONG_PRESS_MS = 400;

// A catalog entry — exact Figma icon + label. `destination` is set for
// the canonical routes wired through `onNavigate`; tool-only entries
// navigate through a host `extras` entry that shares the same `key`.
interface CatalogEntry {
  readonly key: string;
  readonly iconName: NavIconName;
  readonly label: string;
  readonly testId: string;
  readonly destination?: Destination;
}

// Fixed group — always pinned at the top, cannot be unpinned.
const FIXED_CATALOG: readonly CatalogEntry[] = [
  {
    key: "chat",
    iconName: "chat",
    label: "Chat",
    testId: "nav-chat",
    destination: "chat",
  },
  {
    key: "calls",
    iconName: "calls",
    label: "History",
    testId: "nav-history",
    destination: "calls",
  },
  {
    key: "contacts",
    iconName: "contacts",
    label: "Contacts",
    testId: "nav-contacts",
    destination: "contacts",
  },
];

// Pinnable tools — the exact Figma "All tools" set, in design order.
const TOOLS_CATALOG: readonly CatalogEntry[] = [
  {
    key: "directory",
    iconName: "directory",
    label: "Directory",
    testId: "nav-directory",
  },
  {
    key: "jobboard",
    iconName: "jobboard",
    label: "Job Board",
    testId: "nav-jobboard",
  },
  {
    key: "features",
    iconName: "features",
    label: "Features",
    testId: "nav-features",
  },
  { key: "vault", iconName: "vault", label: "Vault", testId: "nav-vault" },
  { key: "events", iconName: "events", label: "Events", testId: "nav-events" },
  {
    key: "calendar",
    iconName: "calendar",
    label: "Calendar",
    testId: "nav-calendar",
  },
];

function formatBadge(value: number): string {
  if (value > 99) return "99+";
  return String(value);
}

// A resolved rail entry — one shape for the rail and the tools menu.
interface RailEntry {
  readonly key: string;
  readonly label: string;
  readonly testId: string;
  readonly iconName: NavIconName;
  readonly active: boolean;
  readonly unread: number;
  readonly onPress: () => void;
}

const NOOP = (): void => {};

// ─── Main component ────────────────────────────────────────────────────────────

export function Sidebar(props: SidebarProps): JSX.Element {
  const {
    current,
    onNavigate,
    unreadCounts,
    accountName,
    accountAvatarUri,
    onAccountPress,
    extras = [],
    pinnedItems = [],
    itemOrder = [],
    onPinItem,
    onUnpinItem,
    onReorderItems,
  } = props;

  const scheme = useColorScheme();
  const fg =
    scheme === "dark"
      ? tokens.color.semantic.dark.icon.default
      : tokens.color.semantic.light.icon.default;

  const [editMode, setEditMode] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const draggingKeyRef = useRef<string | null>(null);
  const dragOverKeyRef = useRef<string | null>(null);
  const toolsAnchorRef = useRef<unknown>(null);

  // Fixed entries — wired straight through onNavigate.
  const resolveFixed = useCallback(
    (c: CatalogEntry): RailEntry => ({
      key: c.key,
      label: c.label,
      testId: c.testId,
      iconName: c.iconName,
      active: c.destination !== undefined && current === c.destination,
      unread:
        c.destination !== undefined ? (unreadCounts?.[c.destination] ?? 0) : 0,
      onPress: () => {
        if (c.destination !== undefined) onNavigate(c.destination);
      },
    }),
    [current, unreadCounts, onNavigate],
  );

  // Tool entries — icon/label from the catalog; navigation comes from a
  // host `extras` entry with the same key (no-op if the host hasn't
  // wired that tool's route yet).
  const resolveTool = useCallback(
    (c: CatalogEntry): RailEntry => {
      const extra = extras.find((e) => e.key === c.key);
      return {
        key: c.key,
        label: c.label,
        testId: c.testId,
        iconName: c.iconName,
        active: extra?.active ?? false,
        unread: extra?.unreadCount ?? 0,
        onPress: extra?.onPress ?? NOOP,
      };
    },
    [extras],
  );

  // Resolve any catalog key (fixed or tool) to a rail entry.
  const resolveKey = useCallback(
    (key: string): RailEntry | null => {
      const fixed = FIXED_CATALOG.find((c) => c.key === key);
      if (fixed) return resolveFixed(fixed);
      const tool = TOOLS_CATALOG.find((t) => t.key === key);
      return tool ? resolveTool(tool) : null;
    },
    [resolveFixed, resolveTool],
  );

  // The REORDERABLE group = the fixed three + every user-pinned tool. All of
  // these can be dragged to reorder; the order is persisted (fixed items
  // included). The unpinned tool shown below the divider is NOT reorderable.
  const reorderableKeys = useMemo(() => {
    const pinnedTools = TOOLS_CATALOG.map((c) => c.key).filter((k) =>
      pinnedItems.includes(k),
    );
    const base = [...FIXED_CATALOG.map((c) => c.key), ...pinnedTools];
    const baseSet = new Set(base);
    const ordered = itemOrder.filter((k) => baseSet.has(k));
    const rest = base.filter((k) => !itemOrder.includes(k));
    return [...ordered, ...rest];
  }, [itemOrder, pinnedItems]);

  // Tools not pinned only surface in the rail while you're visiting them.
  const activeUnpinnedKey = useMemo(() => {
    const unpinnedTools = TOOLS_CATALOG.map((c) => c.key).filter(
      (k) => !pinnedItems.includes(k),
    );
    return (
      unpinnedTools.find(
        (k) => resolveTool(TOOLS_CATALOG.find((t) => t.key === k)!).active,
      ) ?? null
    );
  }, [pinnedItems, resolveTool]);

  const showDivider = activeUnpinnedKey !== null;

  // ── Drag-reorder (web; pointer-capture + elementFromPoint) ──
  const beginDrag = useCallback((key: string) => {
    draggingKeyRef.current = key;
    dragOverKeyRef.current = key;
    setDragOverKey(key);
  }, []);
  const updateDragOver = useCallback((key: string) => {
    if (draggingKeyRef.current === null) return;
    dragOverKeyRef.current = key;
    setDragOverKey(key);
  }, []);
  const endDrag = useCallback(() => {
    const from = draggingKeyRef.current;
    const to = dragOverKeyRef.current;
    draggingKeyRef.current = null;
    dragOverKeyRef.current = null;
    setDragOverKey(null);
    if (from === null || to === null || from === to) return;
    const list = [...reorderableKeys];
    const fromIdx = list.indexOf(from);
    const toIdx = list.indexOf(to);
    if (fromIdx === -1 || toIdx === -1) return;
    list.splice(fromIdx, 1);
    list.splice(toIdx, 0, from);
    onReorderItems?.(list);
  }, [reorderableKeys, onReorderItems]);

  const handleTogglePin = useCallback(
    (key: string) => {
      if (pinnedItems.includes(key)) onUnpinItem?.(key);
      else onPinItem?.(key);
    },
    [pinnedItems, onPinItem, onUnpinItem],
  );

  // Tools menu = fixed (3) + every tool (6) — exactly the Figma catalog.
  const toolsEntries = useMemo(
    () => [
      ...FIXED_CATALOG.map((c) => ({ entry: resolveFixed(c), fixed: true })),
      ...TOOLS_CATALOG.map((c) => ({ entry: resolveTool(c), fixed: false })),
    ],
    [resolveFixed, resolveTool],
  );

  // Draggable (reorderable) rail item — used for the fixed three + pinned tools.
  const renderDraggable = (key: string): React.ReactNode => {
    const entry = resolveKey(key);
    if (!entry) return null;
    return (
      <RailItem
        key={key}
        entry={entry}
        fg={fg}
        customizable
        editMode={editMode}
        dragOver={dragOverKey === key}
        onEnterEdit={() => setEditMode(true)}
        onBeginDrag={() => beginDrag(key)}
        onDragOverKey={updateDragOver}
        onEndDrag={endDrag}
      />
    );
  };

  // Static (non-draggable) rail item — the active unpinned tool below the divider.
  const renderStatic = (key: string): React.ReactNode => {
    const entry = resolveKey(key);
    if (!entry) return null;
    return (
      <RailItem
        key={key}
        entry={entry}
        fg={fg}
        customizable={false}
        editMode={editMode}
      />
    );
  };

  return (
    <View
      accessibilityLabel="Primary navigation"
      accessibilityRole="menu"
      testID="nav-rail"
      className="flex flex-col items-center justify-between w-16 h-full px-2 py-6 select-none bg-light-surface-app dark:bg-dark-surface-app"
    >
      {/* Top: reorderable group (fixed three + pinned tools) → divider →
			    active unpinned tool (static). */}
      <View className="flex flex-col items-center gap-3">
        {reorderableKeys.map((key) => renderDraggable(key))}

        {showDivider && (
          <View className="w-6 h-px bg-light-border-secondary dark:bg-[#2F333B]" />
        )}

        {activeUnpinnedKey !== null && renderStatic(activeUnpinnedKey)}
      </View>

      {/* Bottom: All-tools trigger (or Done in edit mode) + account avatar */}
      <View className="flex flex-col items-center gap-3">
        {editMode ? (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Done editing"
            testID="nav-edit-done"
            onPress={() => setEditMode(false)}
            className="flex items-center justify-center w-12 h-12 rounded-[12px] bg-light-accent-brand dark:bg-dark-accent-brand"
          >
            <Text className="text-[11px] font-semibold text-white">Done</Text>
          </Pressable>
        ) : (
          <Pressable
            ref={toolsAnchorRef as React.Ref<View>}
            accessibilityRole="button"
            accessibilityLabel="All tools"
            accessibilityState={{ expanded: toolsOpen }}
            testID="nav-all-tools"
            onPress={() => setToolsOpen((v) => !v)}
            className={`flex flex-col items-center justify-center w-12 h-12 gap-0.5 rounded-[12px] ${
              toolsOpen
                ? "bg-light-surface-elevated dark:bg-dark-surface-elevated"
                : "bg-transparent hover:bg-light-surface-elevated dark:hover:bg-dark-surface-elevated"
            }`}
          >
            <NavIcon
              name="alltools"
              size={24}
              color={fg}
              accessibilityLabel="All tools"
            />
            <Text
              numberOfLines={1}
              className="text-[10px] leading-[14px] text-light-text-primary dark:text-white"
            >
              All tools
            </Text>
          </Pressable>
        )}

        <AccountAvatar
          name={accountName}
          {...(accountAvatarUri !== undefined ? { uri: accountAvatarUri } : {})}
          {...(onAccountPress !== undefined ? { onPress: onAccountPress } : {})}
        />
      </View>

      {/* Tools menu — opens to the RIGHT of the rail (Figma). */}
      <AnchoredPopover
        anchorRef={toolsAnchorRef}
        open={toolsOpen}
        onClose={() => setToolsOpen(false)}
        placement="right"
        align="end"
        gap={12}
      >
        <ToolsMenu
          entries={toolsEntries}
          pinnedItems={pinnedItems}
          fg={fg}
          onNavigate={(entry) => {
            entry.onPress();
            setToolsOpen(false);
          }}
          onTogglePin={handleTogglePin}
          onClose={() => setToolsOpen(false)}
        />
      </AnchoredPopover>
    </View>
  );
}

// ─── Rail item ────────────────────────────────────────────────────────────────

interface RailItemProps {
  readonly entry: RailEntry;
  readonly fg: string;
  readonly customizable: boolean;
  readonly editMode: boolean;
  readonly dragOver?: boolean;
  readonly onEnterEdit?: (() => void) | undefined;
  readonly onBeginDrag?: (() => void) | undefined;
  readonly onDragOverKey?: ((key: string) => void) | undefined;
  readonly onEndDrag?: (() => void) | undefined;
}

function RailItem(props: RailItemProps): JSX.Element {
  const { entry, fg, customizable, editMode, dragOver = false } = props;
  const { label, testId, active, unread, iconName, onPress } = entry;
  const iconColor = active ? "#FFFFFF" : fg;
  const bgClass = active
    ? "bg-light-accent-brand dark:bg-dark-accent-brand"
    : "bg-transparent hover:bg-light-surface-elevated dark:hover:bg-dark-surface-elevated";

  const visual = (
    <>
      <NavIcon
        name={iconName}
        size={24}
        color={iconColor}
        accessibilityLabel={label}
      />
      <Text
        numberOfLines={1}
        className={`text-[8px] leading-normal text-center ${
          active ? "text-white" : "text-light-text-primary dark:text-white"
        }`}
      >
        {label}
      </Text>
      {!editMode && unread > 0 && (
        <View
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          className="absolute top-0 right-0 min-w-[18px] h-[18px] px-[5px] flex flex-row items-center justify-center rounded-pill bg-light-accent-rose dark:bg-dark-accent-rose"
        >
          <Text className="text-[8px] font-semibold text-white">
            {formatBadge(unread)}
          </Text>
        </View>
      )}
    </>
  );

  const itemClass = `relative flex flex-col items-center justify-center w-12 h-12 gap-0.5 rounded-[12px] ${bgClass} ${
    customizable && editMode
      ? "border border-dashed border-light-border-strong dark:border-[#2F333B]"
      : ""
  }`;

  if (!customizable) {
    return (
      <Pressable
        accessibilityRole="menuitem"
        accessibilityLabel={label}
        accessibilityState={{ selected: active }}
        testID={testId}
        onPress={onPress}
        className={itemClass}
      >
        {visual}
      </Pressable>
    );
  }

  return (
    <CustomizableRailItem
      entry={entry}
      editMode={editMode}
      dragOver={dragOver}
      itemClass={itemClass}
      onEnterEdit={props.onEnterEdit}
      onBeginDrag={props.onBeginDrag}
      onDragOverKey={props.onDragOverKey}
      onEndDrag={props.onEndDrag}
    >
      {visual}
    </CustomizableRailItem>
  );
}

interface CustomizableRailItemProps {
  readonly entry: RailEntry;
  readonly editMode: boolean;
  readonly dragOver: boolean;
  readonly itemClass: string;
  readonly children: React.ReactNode;
  readonly onEnterEdit?: (() => void) | undefined;
  readonly onBeginDrag?: (() => void) | undefined;
  readonly onDragOverKey?: ((key: string) => void) | undefined;
  readonly onEndDrag?: (() => void) | undefined;
}

function CustomizableRailItem({
  entry,
  editMode,
  dragOver,
  itemClass,
  children,
  onEnterEdit,
  onBeginDrag,
  onDragOverKey,
  onEndDrag,
}: CustomizableRailItemProps): JSX.Element {
  const { key, label, testId, active, onPress } = entry;
  const hostRef = useRef<unknown>(null);

  const cbRef = useRef({
    onEnterEdit,
    onBeginDrag,
    onDragOverKey,
    onEndDrag,
    onPress,
    editMode,
  });
  cbRef.current = {
    onEnterEdit,
    onBeginDrag,
    onDragOverKey,
    onEndDrag,
    onPress,
    editMode,
  };

  // Web: long-press → edit, and drag-to-reorder, via REAL DOM pointer
  // events bound to the host node. react-native-web's Pressable swallows
  // synthetic pointer props, so we bypass it. `setPointerCapture` keeps
  // move/up on this node; `elementFromPoint` resolves the hovered item by
  // its `data-railkey`. No-op on native.
  useEffect(() => {
    if (!IS_WEB) return undefined;
    const node = hostRef.current as HTMLElement | null;
    if (node === null || typeof node.addEventListener !== "function")
      return undefined;
    node.setAttribute("data-railkey", key);

    let timer: ReturnType<typeof setTimeout> | null = null;
    let longPress = false;
    let dragging = false;
    const clear = (): void => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };
    const onDown = (e: PointerEvent): void => {
      longPress = false;
      dragging = false;
      try {
        node.setPointerCapture(e.pointerId);
      } catch {
        /* unsupported */
      }
      if (cbRef.current.editMode) {
        dragging = true;
        cbRef.current.onBeginDrag?.();
        return;
      }
      clear();
      timer = setTimeout(() => {
        longPress = true;
        dragging = true;
        cbRef.current.onEnterEdit?.();
        cbRef.current.onBeginDrag?.();
      }, LONG_PRESS_MS);
    };
    const onMove = (e: PointerEvent): void => {
      if (!dragging) return;
      e.preventDefault();
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const wrap = el?.closest("[data-railkey]");
      const overKey = wrap?.getAttribute("data-railkey");
      if (overKey !== null && overKey !== undefined)
        cbRef.current.onDragOverKey?.(overKey);
    };
    const onUp = (e: PointerEvent): void => {
      clear();
      try {
        node.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (dragging) {
        dragging = false;
        cbRef.current.onEndDrag?.();
        return;
      }
      if (!longPress && !cbRef.current.editMode) cbRef.current.onPress();
    };
    const onCancel = (): void => {
      clear();
      if (dragging) {
        dragging = false;
        cbRef.current.onEndDrag?.();
      }
    };

    node.addEventListener("pointerdown", onDown);
    node.addEventListener("pointermove", onMove);
    node.addEventListener("pointerup", onUp);
    node.addEventListener("pointercancel", onCancel);
    return () => {
      clear();
      node.removeEventListener("pointerdown", onDown);
      node.removeEventListener("pointermove", onMove);
      node.removeEventListener("pointerup", onUp);
      node.removeEventListener("pointercancel", onCancel);
    };
  }, [key]);

  return (
    <Pressable
      ref={hostRef as React.Ref<View>}
      accessibilityRole="menuitem"
      accessibilityLabel={label}
      accessibilityState={{ selected: active }}
      testID={testId}
      onPress={IS_WEB ? undefined : onPress}
      onLongPress={IS_WEB ? undefined : onEnterEdit}
      delayLongPress={LONG_PRESS_MS}
      className={`${itemClass} ${dragOver && editMode ? "opacity-40" : editMode ? "opacity-90" : ""}`}
    >
      {children}
    </Pressable>
  );
}

// ─── Tools menu (Figma 3393:217233 / 4582:82601) ───────────────────────────────

interface ToolsMenuProps {
  readonly entries: readonly { entry: RailEntry; fixed: boolean }[];
  readonly pinnedItems: readonly string[];
  readonly fg: string;
  readonly onNavigate: (entry: RailEntry) => void;
  readonly onTogglePin: (key: string) => void;
  readonly onClose: () => void;
}

function ToolsMenu({
  entries,
  pinnedItems,
  fg,
  onNavigate,
  onTogglePin,
  onClose,
}: ToolsMenuProps): JSX.Element {
  return (
    <View className="w-[300px] p-4 gap-2.5 select-none rounded-3xl bg-light-surface-panel dark:bg-[#11131A] border border-light-border-primary dark:border-[#2F333B]">
      {/* Header — centered title + close X */}
      <View className="flex flex-row items-center justify-between h-[22px]">
        <View className="w-[18px]" />
        <Text className="text-[16px] font-semibold text-[#6B7280] dark:text-[#B5BAC3]">
          All tools
        </Text>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Close tools menu"
          onPress={onClose}
        >
          <X size={18} strokeWidth={2} color={fg} />
        </Pressable>
      </View>

      {/* Grid — 4 per row, 56×56 items, 12px gaps */}
      <View className="flex flex-row flex-wrap gap-3">
        {entries.map(({ entry, fixed }) => (
          <ToolsMenuItem
            key={entry.key}
            entry={entry}
            fg={fg}
            pinned={fixed || pinnedItems.includes(entry.key)}
            canToggle={!fixed}
            onNavigate={() => onNavigate(entry)}
            onTogglePin={() => onTogglePin(entry.key)}
          />
        ))}
      </View>

      {/* Footer hint */}
      <View className="h-[14px]">
        <Text className="text-[10px] leading-[14px] text-center text-[#6B7280]">
          Right- click any icon to pin or unpin
        </Text>
      </View>
    </View>
  );
}

interface ToolsMenuItemProps {
  readonly entry: RailEntry;
  readonly fg: string;
  readonly pinned: boolean;
  readonly canToggle: boolean;
  readonly onNavigate: () => void;
  readonly onTogglePin: () => void;
}

function ToolsMenuItem({
  entry,
  fg,
  pinned,
  canToggle,
  onNavigate,
  onTogglePin,
}: ToolsMenuItemProps): JSX.Element {
  const { label, active, iconName } = entry;
  const [optionsOpen, setOptionsOpen] = useState(false);
  const anchorRef = useRef<unknown>(null);

  const iconColor = active ? "#FFFFFF" : fg;
  // Right-clicked item gets the elevated surface (Figma shows the target
  // tile filled while its pin popover is open).
  const bgClass = active
    ? "bg-light-accent-brand dark:bg-dark-accent-brand"
    : optionsOpen
      ? "bg-light-surface-elevated dark:bg-[#222429]"
      : "bg-transparent hover:bg-light-surface-elevated dark:hover:bg-[#222429]";

  const openOptions = useCallback(() => {
    if (canToggle) setOptionsOpen(true);
  }, [canToggle]);

  // Web right-click → Pin/Unpin popover (suppress browser menu). Bound
  // imperatively since `onContextMenu` isn't an RN prop.
  useEffect(() => {
    if (!IS_WEB || !canToggle) return undefined;
    const node = anchorRef.current as HTMLElement | null;
    if (node === null || typeof node.addEventListener !== "function")
      return undefined;
    const handler = (e: Event): void => {
      e.preventDefault();
      setOptionsOpen(true);
    };
    node.addEventListener("contextmenu", handler);
    return () => node.removeEventListener("contextmenu", handler);
  }, [canToggle]);

  return (
    <View className="relative">
      <Pressable
        ref={anchorRef as React.Ref<View>}
        testID={`nav-tool-${entry.key}`}
        accessibilityRole="menuitem"
        accessibilityLabel={label}
        accessibilityState={{ selected: active }}
        onPress={onNavigate}
        onLongPress={openOptions}
        delayLongPress={LONG_PRESS_MS}
        className={`flex flex-col items-center justify-center w-14 h-14 gap-0.5 rounded-[12px] ${bgClass}`}
      >
        <NavIcon
          name={iconName}
          size={24}
          color={iconColor}
          accessibilityLabel={label}
        />
        <Text
          numberOfLines={1}
          className="text-[10px] leading-[14px] text-center text-light-text-primary dark:text-white"
        >
          {label}
        </Text>

        {/* Pin badge — only on pinned items (top-right, Figma 12×12 r=4) */}
        {pinned && (
          <View className="absolute top-1 right-1 w-3 h-3 flex items-center justify-center rounded-[4px] bg-light-surface-elevated dark:bg-[#222429]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="8"
              height="8"
              viewBox="0 0 8 8"
              fill="none"
            >
              <path
                d="M2.5789 5.42108L0.5 7.5M2.5789 5.42108L1.27691 4.11909C1.02454 3.86669 0.898354 3.74052 0.881416 3.61833C0.866657 3.5119 0.899225 3.40433 0.970546 3.32396C1.05241 3.23169 1.2274 3.1967 1.57737 3.1267L2.92407 2.85736C2.98896 2.84439 3.0214 2.8379 3.05165 2.82603C3.07851 2.8155 3.10403 2.80184 3.12769 2.78534C3.15435 2.76675 3.17775 2.74335 3.22453 2.69656L3.79817 2.12293C3.82808 2.09301 3.84304 2.07806 3.85608 2.06161C3.8677 2.04701 3.87817 2.03154 3.88743 2.01534C3.89783 1.99711 3.90617 1.97766 3.92286 1.93877L4.31572 1.02209C4.43031 0.754661 4.48764 0.620945 4.58025 0.560204C4.66121 0.507089 4.75988 0.488086 4.85481 0.507328C4.96333 0.529331 5.06619 0.632198 5.27196 0.837937L7.16206 2.72805C7.36779 2.93379 7.47065 3.03666 7.49266 3.1452C7.51192 3.24011 7.49292 3.33879 7.43978 3.41977C7.37907 3.51238 7.24533 3.56967 6.9779 3.6843L6.06121 4.07716C6.02232 4.09384 6.00288 4.10215 5.98465 4.11258C5.96845 4.12184 5.95298 4.13232 5.93839 4.14389C5.92196 4.15698 5.90697 4.17193 5.87705 4.20185L5.30345 4.77548C5.25663 4.82227 5.23326 4.84564 5.21467 4.87232C5.19816 4.89599 5.18449 4.92149 5.17398 4.94836C5.16211 4.9786 5.15561 5.01105 5.14263 5.07592L4.87329 6.42263C4.80328 6.7726 4.7683 6.94761 4.67605 7.02945C4.59568 7.10078 4.48811 7.13334 4.38165 7.11861C4.25949 7.10166 4.13329 6.97546 3.88093 6.7231L2.5789 5.42108Z"
                stroke="#8C929E"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </View>
        )}
      </Pressable>

      {/* Pin / Unpin options popover (Figma options-menu 4582:82734) */}
      <AnchoredPopover
        anchorRef={anchorRef}
        open={optionsOpen}
        onClose={() => setOptionsOpen(false)}
        placement="bottom"
        align="start"
        gap={6}
      >
        <View className="p-1 min-w-[133px] rounded-[12px] bg-light-surface-panel dark:bg-[#15171C] border border-light-border-primary dark:border-[#2F333B]">
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={pinned ? `Unpin ${label}` : `Pin ${label}`}
            onPress={() => {
              onTogglePin();
              setOptionsOpen(false);
            }}
            className="flex flex-row items-center gap-3 px-3 py-1 rounded-lg hover:bg-light-surface-elevated dark:hover:bg-dark-surface-elevated"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill={pinned ? fg : "transparent"}
            >
              <path
                d="M5.47366 10.5264L1.77783 14.2223M5.47366 10.5264L3.159 8.21177C2.71034 7.76305 2.48602 7.53876 2.4559 7.32153C2.42967 7.13232 2.48756 6.94109 2.61436 6.7982C2.7599 6.63418 3.07098 6.57196 3.69315 6.44753L6.08729 5.9687C6.20265 5.94563 6.26032 5.93409 6.31411 5.913C6.36185 5.89427 6.40722 5.86999 6.44928 5.84065C6.49667 5.80761 6.53827 5.76602 6.62144 5.68283L7.64124 4.66304C7.69442 4.60986 7.72101 4.58326 7.7442 4.55403C7.76485 4.52806 7.78347 4.50057 7.79993 4.47177C7.81842 4.43935 7.83325 4.40479 7.86291 4.33565L8.56133 2.706C8.76504 2.23056 8.86696 1.99285 9.0316 1.88486C9.17553 1.79044 9.35095 1.75665 9.51971 1.79086C9.71264 1.82998 9.89551 2.01285 10.2613 2.37861L13.6215 5.73881C13.9872 6.10457 14.1701 6.28744 14.2092 6.48041C14.2435 6.64915 14.2097 6.82457 14.1152 6.96853C14.0073 7.13317 13.7695 7.23503 13.2941 7.4388L11.6644 8.13722C11.5953 8.16688 11.5607 8.18165 11.5283 8.2002C11.4995 8.21667 11.472 8.23529 11.4461 8.25587C11.4169 8.27912 11.3902 8.30572 11.337 8.3589L10.3173 9.37869C10.2341 9.46186 10.1925 9.50341 10.1595 9.55085C10.1301 9.59292 10.1058 9.63826 10.0871 9.68602C10.066 9.73979 10.0545 9.79748 10.0314 9.9128L9.55257 12.307C9.42811 12.9291 9.36592 13.2402 9.20193 13.3857C9.05904 13.5126 8.86781 13.5704 8.67854 13.5442C8.46137 13.5141 8.23701 13.2898 7.78837 12.8411L5.47366 10.5264Z"
                stroke={fg}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <Text className="text-[14px] text-light-text-primary dark:text-[#F5F6F8]">
              {pinned ? "Unpin" : "Pin"}
            </Text>
          </Pressable>
        </View>
      </AnchoredPopover>
    </View>
  );
}

// ─── Account avatar ───────────────────────────────────────────────────────────

interface AccountAvatarProps {
  readonly name: string;
  readonly uri?: string;
  readonly onPress?: () => void;
}

function AccountAvatar({
  name,
  uri,
  onPress,
}: AccountAvatarProps): JSX.Element {
  const c = tokens.color.semantic.light;
  const initial = name.trim().charAt(0).toUpperCase() || "";
  const tile = (
    <View className="w-10 h-10 rounded-[12px] overflow-hidden items-center justify-center bg-light-surface-elevated dark:bg-dark-surface-elevated">
      {uri !== undefined ? (
        <Image
          source={{ uri }}
          accessibilityIgnoresInvertColors
          className="w-10 h-10"
        />
      ) : (
        <LinearGradient
          colors={["rgba(150,154,162,0.30)", "rgba(150,154,162,0.05)"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          className="flex w-10 h-10 items-center justify-center"
        >
          <Text className="font-sans font-semibold text-light-text-primary dark:text-dark-text-primary">
            {initial}
          </Text>
        </LinearGradient>
      )}
    </View>
  );

  const wrappedTile = (
    <View
      className="relative"
      accessibilityLabel={`Account: ${name || "profile"}`}
      testID="nav-account"
    >
      {tile}
      <View
        className="absolute -right-[1px] -bottom-[1px] w-2 h-2 rounded-full border-2"
        style={{
          borderColor: c.surface.app,
          backgroundColor: c.presence.online,
        }}
      />
    </View>
  );

  if (onPress === undefined) return wrappedTile;
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`Account: ${name || "profile"}`}
      testID="nav-account"
      onPress={onPress}
    >
      {wrappedTile}
    </Pressable>
  );
}
